import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Copy, KeyRound, Plus, RefreshCw, ShieldAlert, TriangleAlert } from "lucide-react";

import { apiGet, apiPost, unpackList } from "../../Api/Client.js";
import { EmptyState, Panel, StatCard, StatusPill } from "../../Shared/ScreenComponents.jsx";
import { formatDateTime } from "../../Shared/ScreenUtils.jsx";
import { McpTokensPanel } from "./McpTokensPanel.jsx";

/* AXON API — every employee mints and manages their own GATE keys here.
 *
 * The rules below are ENFORCED BY THE SERVER; this screen only reflects them
 * honestly so nobody fills in a form that was never going to be accepted:
 *
 *   - a key binds to a project you are actually assigned to, so the project
 *     field is a <select> fed by the server, never free text;
 *   - a written purpose is required;
 *   - one wallet per person per month, shared across at most five active keys;
 *   - the raw token exists in exactly one response, once, and is never stored.
 *
 * Client-side headroom arithmetic is UX. The server re-validates all of it.
 */

const AXON_BASE = "/AxonApi";
const FALLBACK_CAP = 50;
const FALLBACK_MAX_KEYS = 5;

function num(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function usd(value) {
  return `$${num(value).toFixed(2)}`;
}

/* The reset date is the server's to state — it owns the period boundary. This
   only fills in the obvious answer (first of next month, UTC, matching GATE's
   own period roll) when the payload didn't say, so the header never reads
   "resets —" and leaves people guessing whether the wallet resets at all. */
function fallbackResetDate() {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
}

function formatReset(value) {
  const date = value ? new Date(value) : fallbackResetDate();
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

/* A key is shown by its prefix and nothing else. The full token is rendered in
   exactly one place in this file — the reveal panel, straight from the mint
   response — and is never put in state that outlives the dismissal. */
function maskedToken(key) {
  const prefix = (key.token_prefix || "").trim();
  return prefix ? `${prefix}…` : "—";
}

function errorMessage(error, fallback) {
  return (
    error?.payload?.detail ||
    error?.payload?.error ||
    error?.payload?.message ||
    error?.message ||
    fallback
  );
}

/* Copy without window.prompt as a fallback — a modal browser dialog in the
   middle of a "this is the only time you will see this" panel is the worst
   possible moment to steal focus. If the clipboard is unavailable we say so
   and leave the token selectable on screen. */
function TokenCopyButton({ value, onFail }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      onFail?.("Your browser blocked clipboard access — select the token above and copy it manually.");
      return;
    }
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <button type="button" className="Primary-Button Small" onClick={copy}>
      {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy token"}
    </button>
  );
}

/* Shown once, immediately after a successful mint, above everything else.
   Dismissing it is the only way it leaves the page, and it does not come back. */
function TokenReveal({ minted, onDismiss }) {
  const [problem, setProblem] = useState("");
  return (
    <Panel
      title="Your new API key"
      subtitle={minted.label ? `Key ${minted.label}` : undefined}
      right={<button className="Soft-Button Small" onClick={onDismiss}>Done</button>}
      className="Axon-Token-Reveal"
    >
      <p style={{ margin: "0 0 10px", fontWeight: 600, color: "var(--danger-text)" }}>
        <TriangleAlert size={14} style={{ verticalAlign: "-2px" }} /> This will not be shown again.
      </p>
      <p style={{ margin: "0 0 12px", color: "var(--text-muted)", fontSize: 13 }}>
        Copy it into your environment now. The intranet does not store it, and there is no way to
        retrieve it later — if you lose it, revoke the key and mint a new one.
      </p>
      <code
        style={{
          display: "block",
          padding: "12px 14px",
          borderRadius: 10,
          background: "var(--surface-sunken)",
          border: "1px solid var(--border-subtle)",
          fontFamily: "var(--font-mono)",
          fontSize: 13,
          wordBreak: "break-all",
          userSelect: "all",
        }}
      >
        {minted.token}
      </code>
      <div className="Table-Actions" style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
        <TokenCopyButton value={minted.token} onFail={setProblem} />
      </div>
      {problem && <p style={{ margin: "10px 0 0", fontSize: 13, color: "var(--danger-text)" }}>{problem}</p>}
    </Panel>
  );
}

export function AxonApiScreen() {
  const [projects, setProjects] = useState([]);
  const [keys, setKeys] = useState([]);
  const [wallet, setWallet] = useState(null);

  // "loading" | "ready" | "gate_down" | "error" — gate_down is deliberately its
  // own state. A 503 from GATE used to be indistinguishable from an empty list,
  // which reads as "you have no keys" and invites someone to mint a duplicate.
  const [state, setState] = useState("loading");
  const [loadError, setLoadError] = useState("");
  const [staleSpend, setStaleSpend] = useState(false);
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState("");

  const [minted, setMinted] = useState(null);
  const [form, setForm] = useState({ project: "", purpose: "", allocation: "" });
  const [confirmRevoke, setConfirmRevoke] = useState(null);
  const [resizing, setResizing] = useState(null);

  const load = useCallback(async () => {
    setLoadError("");
    try {
      const payload = await apiGet(`${AXON_BASE}/keys/`);
      setKeys(unpackList(payload, ["keys"]));
      setWallet(payload?.wallet || payload?.summary || null);
      // The server can answer with the rows it holds but no live spend when
      // GATE is slow. That is a caveat on the numbers, not a dead page.
      // The contract is `gate: {reachable, detail}` — see _wallet_payload's
      // sibling `gate_state` in Backend/Apps/AxonApi/views.py. The two flat
      // flags are tolerated as older shapes; without the `gate` check the
      // banner never fires and spend silently renders as "—" with no reason.
      setStaleSpend(Boolean(
        payload?.gate?.reachable === false ||
        payload?.gate_unreachable || payload?.spend_unavailable
      ));
      setState("ready");
    } catch (error) {
      if (error?.status === 503) {
        setState("gate_down");
        setLoadError(errorMessage(error, "AXON API can't reach GATE right now."));
      } else {
        setState("error");
        setLoadError(errorMessage(error, "Unable to load your API keys."));
      }
      return;
    }

    // Losing the project list must not blank the keys above it — you can still
    // read and revoke what you hold, you just can't mint until it comes back.
    try {
      setProjects(unpackList(await apiGet(`${AXON_BASE}/projects/`), ["projects"]));
    } catch {
      setProjects([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const activeKeys = useMemo(
    () => keys.filter((key) => (key.status || "Active") !== "Revoked"),
    [keys]
  );

  /* The wallet the server sent, with a locally derived stand-in for anything it
     did not send. Server figures always win — this is a display fallback, never
     a second opinion. */
  const summary = useMemo(() => {
    const cap = num(wallet?.cap_usd ?? wallet?.monthly_cap_usd ?? wallet?.cap, FALLBACK_CAP);
    const carried = num(
      wallet?.carried_usd ?? wallet?.carried ??
      activeKeys.reduce((total, key) => total + num(key.allocation_usd), 0)
    );
    const forfeited = num(wallet?.forfeited_usd ?? wallet?.forfeited);
    // The server calls this `used_usd` (carried + forfeited). The arithmetic
    // fallback happens to agree, but relying on that coincidence means a
    // server-side change to the definition would drift here silently — on a
    // page about money, read the field the server actually sends.
    const committed = num(wallet?.used_usd ?? wallet?.committed_usd, carried + forfeited);
    const remaining = num(wallet?.remaining_usd, Math.max(0, cap - committed));
    const spent = num(
      wallet?.spent_usd ?? keys.reduce((total, key) => total + num(key.spent_usd), 0)
    );
    const maxKeys = num(wallet?.max_active_keys ?? wallet?.max_keys, FALLBACK_MAX_KEYS);
    const activeCount = num(wallet?.active_key_count, activeKeys.length);
    return {
      cap,
      carried,
      forfeited,
      committed,
      remaining,
      spent,
      maxKeys,
      activeCount,
      period: wallet?.period || "",
      resetsOn: wallet?.resets_on || wallet?.next_reset_at || wallet?.period_ends_at || "",
      pct: cap > 0 ? Math.min(100, Math.round((committed / cap) * 100)) : 0,
    };
  }, [wallet, keys, activeKeys]);

  const atKeyLimit = summary.activeCount >= summary.maxKeys;
  const amount = num(form.allocation, NaN);
  const amountOverHeadroom = Number.isFinite(amount) && amount > summary.remaining + 1e-9;
  const canMint =
    state === "ready" &&
    !atKeyLimit &&
    Boolean(form.project) &&
    Boolean(form.purpose.trim()) &&
    Number.isFinite(amount) &&
    amount > 0 &&
    !amountOverHeadroom &&
    projects.length > 0;

  // Returns whether it worked, so a failed action can leave its confirmation
  // open — closing the row on failure hides the thing you were about to retry.
  const runMutation = async (tag, fn, successNotice) => {
    setBusy(tag);
    setNotice("");
    let ok = false;
    try {
      await fn();
      ok = true;
      if (successNotice) setNotice(successNotice);
    } catch (error) {
      setNotice(
        error?.status === 503
          ? `${errorMessage(error, "AXON API can't reach GATE right now.")} Nothing was changed — try again in a moment.`
          : errorMessage(error, "That didn't work. Try again.")
      );
    }
    await load();
    setBusy("");
    return ok;
  };

  const mint = async () => {
    setBusy("mint");
    setNotice("");
    try {
      const result = await apiPost(`${AXON_BASE}/keys/`, {
        project_id: form.project,
        purpose: form.purpose.trim(),
        allocation_usd: amount,
      });
      const token = result?.token || result?.key?.token;
      if (token) {
        setMinted({ token, label: result?.gate_label || result?.key?.gate_label || "" });
        setForm({ project: "", purpose: "", allocation: "" });
      } else {
        // A mint with no token in the body is not a success we can hand over:
        // say so rather than silently pretending the key was created.
        setNotice("The key was created but no token came back. Revoke it and try again.");
      }
      await load();
    } catch (error) {
      setNotice(
        error?.status === 503
          ? `${errorMessage(error, "AXON API can't reach GATE right now.")} Your key may not have been created — reload before retrying.`
          : errorMessage(error, "Unable to mint that key.")
      );
      await load();
    } finally {
      setBusy("");
    }
  };

  const revoke = async (key) => {
    const ok = await runMutation(
      `revoke-${key.id}`,
      () => apiPost(`${AXON_BASE}/keys/${key.id}/revoke/`, {}),
      "Key revoked. Anything still using it will start failing immediately."
    );
    if (ok) setConfirmRevoke(null);
  };

  const resize = async (key, value) => {
    const ok = await runMutation(
      `resize-${key.id}`,
      () => apiPost(`${AXON_BASE}/keys/${key.id}/resize/`, { allocation_usd: num(value) }),
      "Budget updated."
    );
    if (ok) setResizing(null);
  };

  if (state === "loading") {
    return (
      <section className="Axon-Api-Screen Screen-Stack">
        <Panel title="AXON API"><p style={{ color: "var(--text-muted)" }}>Loading your keys…</p></Panel>
      </section>
    );
  }

  return (
    <section className="Axon-Api-Screen Screen-Stack">
      <header className="ds-page-header">
        <div>
          <span className="ds-page-header__eyebrow">AXON</span>
          <h1 className="ds-page-header__title"><KeyRound size={22} strokeWidth={1.8} style={{ verticalAlign: "-3px" }} /> AXON API</h1>
          <p className="ds-page-header__sub">
            Your own API keys for AXON models, metered by GATE. One key, one project, one budget.
            Below them, your tokens for connecting Claude Code to the intranet.
          </p>
        </div>
        <div className="ds-page-header__actions">
          <button className="Soft-Button Small" onClick={load} disabled={Boolean(busy)}>
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </header>

      {/* GATE unreachable is a retry, not an empty state. Nothing below renders,
          because showing an empty key list here is the exact lie this avoids. */}
      {state === "gate_down" && (
        <Panel title="Can't reach GATE">
          <div style={{ display: "grid", gap: 12, justifyItems: "start" }}>
            <p style={{ margin: 0, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <ShieldAlert size={18} style={{ flexShrink: 0, color: "var(--warning-text)" }} />
              <span>
                {loadError} <strong>This does not mean you have no keys</strong> — the metering
                service didn't answer, so we can't show you what you hold. Any key you already
                have is unaffected. Try again in a moment.
              </span>
            </p>
            <button className="Primary-Button Small" onClick={load}>
              <RefreshCw size={14} /> Try again
            </button>
          </div>
        </Panel>
      )}

      {state === "error" && (
        <Panel title="Something went wrong">
          <div style={{ display: "grid", gap: 12, justifyItems: "start" }}>
            <p style={{ margin: 0 }}>{loadError}</p>
            <button className="Primary-Button Small" onClick={load}>
              <RefreshCw size={14} /> Try again
            </button>
          </div>
        </Panel>
      )}

      {state === "ready" && (
        <>
          {minted && <TokenReveal minted={minted} onDismiss={() => setMinted(null)} />}

          {notice && (
            <div
              role="status"
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                background: "var(--surface-sunken)",
                border: "1px solid var(--border-subtle)",
                fontSize: 13,
              }}
            >
              {notice}
            </div>
          )}

          {staleSpend && (
            <div
              role="status"
              style={{
                padding: "10px 14px",
                borderRadius: 10,
                background: "var(--warning-soft)",
                color: "var(--warning-text)",
                fontSize: 13,
              }}
            >
              GATE didn't answer, so the spend figures below are the last ones we recorded, not live.
            </div>
          )}

          {/* 1. Wallet header */}
          <Panel
            title="This month"
            subtitle={summary.period ? `Period ${summary.period} · resets ${formatReset(summary.resetsOn)}` : `Resets ${formatReset(summary.resetsOn)}`}
          >
            <div className="Stat-Grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
              <StatCard label="Allocated to keys" value={usd(summary.carried)} />
              <StatCard label="Actually spent" value={usd(summary.spent)} />
              <StatCard label="Left to allocate" value={usd(summary.remaining)} />
              <StatCard label="Active keys" value={`${summary.activeCount}/${summary.maxKeys}`} />
            </div>

            <div style={{ marginTop: 14 }}>
              <div className="progress" style={{ width: "100%" }}>
                <span style={{ width: `${summary.pct}%` }} />
              </div>
              <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
                <strong>{usd(summary.committed)} of {usd(summary.cap)}</strong> used this month —{" "}
                {usd(summary.remaining)} left. Your $
                {num(summary.cap).toFixed(0)} is shared across at most {summary.maxKeys} active keys.
              </p>
              {summary.forfeited > 0 && (
                <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--text-muted)" }}>
                  {usd(summary.forfeited)} of that is spend on keys you revoked this month. Revoking
                  a key does not give its spend back, so it still counts until the period resets.
                </p>
              )}
            </div>
          </Panel>

          {/* 2. Key list */}
          <Panel title="Your keys" subtitle={`${activeKeys.length} active`}>
            <div className="Table-Wrap">
              <table className="Erp-Table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Purpose</th>
                    <th>Key</th>
                    <th>Budget</th>
                    <th>Spent</th>
                    <th>Left</th>
                    <th>Created</th>
                    <th>Last used</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {keys.map((key) => {
                    const revoked = (key.status || "Active") === "Revoked";
                    const pending = (key.status || "") === "Pending";
                    const allocation = num(key.allocation_usd);
                    const spent = num(key.spent_usd);
                    const left = num(key.remaining_usd, Math.max(0, allocation - spent));
                    const rowBusy = busy === `revoke-${key.id}` || busy === `resize-${key.id}`;
                    return (
                      <React.Fragment key={key.id}>
                        <tr>
                          <td><strong>{key.project_name || key.project_code || key.project || "—"}</strong></td>
                          <td style={{ maxWidth: 260 }}>{key.purpose || "—"}</td>
                          <td><code style={{ fontFamily: "var(--font-mono)", fontSize: 12 }}>{maskedToken(key)}</code></td>
                          <td>{usd(allocation)}</td>
                          <td>{staleSpend ? "—" : usd(spent)}</td>
                          <td>{staleSpend ? "—" : usd(left)}</td>
                          <td>{formatDateTime(key.created_at) || "—"}</td>
                          <td>{key.last_seen_at ? formatDateTime(key.last_seen_at) : "Never"}</td>
                          <td>
                            <StatusPill tone={revoked ? "red" : pending ? "gold" : "green"}>
                              {key.status || "Active"}
                            </StatusPill>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            {!revoked && (
                              <div style={{ display: "flex", gap: 8 }}>
                                <button
                                  className="Soft-Button Small"
                                  disabled={rowBusy || Boolean(busy)}
                                  onClick={() => {
                                    setConfirmRevoke(null);
                                    setResizing({ id: key.id, value: String(allocation) });
                                  }}
                                >
                                  Resize
                                </button>
                                <button
                                  className="Soft-Button Small danger"
                                  disabled={rowBusy || Boolean(busy)}
                                  onClick={() => {
                                    setResizing(null);
                                    setConfirmRevoke(key.id);
                                  }}
                                >
                                  Revoke
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>

                        {/* Revoke confirmation, in the page. Never window.confirm —
                            a browser dialog gives no room to say what is actually
                            lost, which here is the key's spend for the month. */}
                        {confirmRevoke === key.id && (
                          <tr>
                            <td colSpan={10} style={{ background: "var(--danger-soft)" }}>
                              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                                <span style={{ fontSize: 13 }}>
                                  Revoke this key? Anything using it stops working straight away, and
                                  the {usd(spent)} it has already spent stays counted against your
                                  wallet until {formatReset(summary.resetsOn)}. This cannot be undone —
                                  a replacement key gets a new token.
                                </span>
                                <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                                  <button className="Soft-Button Small" disabled={Boolean(busy)} onClick={() => setConfirmRevoke(null)}>
                                    Keep it
                                  </button>
                                  <button className="Primary-Button Small" disabled={Boolean(busy)} onClick={() => revoke(key)}>
                                    {busy === `revoke-${key.id}` ? "Revoking…" : "Yes, revoke"}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}

                        {resizing?.id === key.id && (
                          <tr>
                            <td colSpan={10} style={{ background: "var(--surface-sunken)" }}>
                              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                                <label style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13 }}>
                                  New budget ($)
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    style={{ width: 120 }}
                                    value={resizing.value}
                                    onChange={(event) => setResizing({ id: key.id, value: event.target.value })}
                                  />
                                </label>
                                <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
                                  Up to {usd(allocation + summary.remaining)}.
                                  {spent > 0 && ` It can't go below the ${usd(spent)} this key has already spent.`}
                                  {" "}Lowering it frees the difference for another key.
                                </span>
                                <div style={{ display: "flex", gap: 8, marginLeft: "auto" }}>
                                  <button className="Soft-Button Small" disabled={Boolean(busy)} onClick={() => setResizing(null)}>
                                    Cancel
                                  </button>
                                  <button
                                    className="Primary-Button Small"
                                    disabled={
                                      Boolean(busy) ||
                                      !Number.isFinite(num(resizing.value, NaN)) ||
                                      num(resizing.value) <= 0 ||
                                      num(resizing.value) > allocation + summary.remaining + 1e-9
                                    }
                                    onClick={() => resize(key, resizing.value)}
                                  >
                                    {busy === `resize-${key.id}` ? "Saving…" : "Save"}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {!keys.length && <EmptyState label="You haven't minted any keys yet. Use the form below to make your first one." />}
          </Panel>

          {/* 3. Mint */}
          <Panel title="Mint a key" subtitle="One key belongs to one project and one budget.">
            {atKeyLimit && (
              <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--warning-text)" }}>
                You already hold {summary.activeCount} of {summary.maxKeys} active keys. Revoke one
                before minting another.
              </p>
            )}
            {!projects.length && (
              <p style={{ margin: "0 0 12px", fontSize: 13, color: "var(--text-muted)" }}>
                You aren't on any active project right now, so there is nothing to bind a key to.
                A key can only be issued against a project you are assigned to — ask your PM to add
                you to the project team, then reload this page.
              </p>
            )}

            <div className="Form-Grid Two">
              <label className="ds-field">
                <span className="ds-label">Project</span>
                <select
                  value={form.project}
                  disabled={!projects.length || atKeyLimit}
                  onChange={(event) => setForm({ ...form, project: event.target.value })}
                >
                  <option value="">Select a project</option>
                  {projects.map((project) => (
                    <option key={project.id} value={project.id}>
                      {project.name || project.title || project.code || `Project ${project.id}`}
                    </option>
                  ))}
                </select>
              </label>
              <label className="ds-field">
                <span className="ds-label">Monthly budget ($)</span>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  max={summary.remaining || undefined}
                  placeholder="0.00"
                  disabled={!projects.length || atKeyLimit}
                  value={form.allocation}
                  onChange={(event) => setForm({ ...form, allocation: event.target.value })}
                />
              </label>
            </div>

            <label className="ds-field" style={{ marginBottom: 12 }}>
              <span className="ds-label">What is this key for? (required)</span>
              <textarea
                rows={3}
                placeholder="e.g. Nightly transcript summarisation for the ingestion job"
                disabled={!projects.length || atKeyLimit}
                value={form.purpose}
                onChange={(event) => setForm({ ...form, purpose: event.target.value })}
              />
            </label>

            <p style={{ margin: "0 0 12px", fontSize: 13, color: amountOverHeadroom ? "var(--danger-text)" : "var(--text-muted)" }}>
              {amountOverHeadroom
                ? `That's more than you have left. ${usd(summary.remaining)} is available this month.`
                : `${usd(summary.remaining)} left to allocate this month.`}
            </p>

            <button className="Primary-Button" disabled={!canMint || Boolean(busy)} onClick={mint}>
              <Plus size={14} /> {busy === "mint" ? "Minting…" : "Mint key"}
            </button>
            <p style={{ margin: "10px 0 0", fontSize: 12, color: "var(--text-muted)" }}>
              The token is shown once, right after minting. It is never stored here and cannot be
              looked up again.
            </p>
          </Panel>
        </>
      )}

      {/* Not tied to GATE: MCP tokens work whether or not GATE answers. */}
      <McpTokensPanel />
    </section>
  );
}

export default AxonApiScreen;
